import { useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AddClientIcon, CampaignIcon } from '../../assets';
import { PERMISSIONS } from '../../config/constants';
import { replaceIds, ROUTES } from '../../config/routes/config';
import { CREATE_CAMPAIGN } from '../../graphql/campaign/mutation';
import { usePermissions } from '../../hooks/usePermissions';
import type { ResCreateCampaignType } from '../../types';
import { Button } from '../common/Button';
import { ModalPortal } from '../common/Portal';
import { BrandImage } from '../images/BrandImage';
import { BrandGroupDropdown } from '../inputs/BrandGroupDropdown';
import type {
  ClientBrandItem,
  ClientSearchPanelHandle,
} from '../inputs/ClientSearchPanel';
import { ClientSearchPanel } from '../inputs/ClientSearchPanel';
import { DatePickerInput } from '../inputs/DatePicker';
import { TextInput } from '../inputs/TextInput';
import { UserDropdownList } from '../inputs/UserDropdownList';
import { BrandPopUp } from './BrandPopUp';
import { PopupLayout } from './PopupLayout';

type TCreateCampaignForm = {
  name: string;
  influencers: Array<{ value: string; id: string }>;
  accountManagers: Array<{ value: string; id: string }>;
  month: string;
  group_id: string;
};

export const CreateCampaign = ({ refetch }: { refetch: any }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<ClientBrandItem | null>(
    null,
  );
  const [openCreateBrand, setOpenCreateBrand] = useState(false);
  const clientSearchPanelRef = useRef<ClientSearchPanelHandle>(null);

  const { ...methods } = useForm<TCreateCampaignForm>();
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  const [createCampaign, { loading }] =
    useMutation<ResCreateCampaignType>(CREATE_CAMPAIGN);
  const { checkPermission } = usePermissions();

  const onSubmit = (data: TCreateCampaignForm) => {
    if (!selectedBrand) return;
    createCampaign({
      variables: {
        name: data.name,
        brand_id: selectedBrand.id,
        influencers: data.influencers.map((i) => i.value) || [],
        accountManagers: data.accountManagers.map((i) => i.value) || [],
        month: data.month ? dayjs(data.month).format('MM/YYYY') : '',
        group_id: data.group_id,
      },
      onCompleted: (res) => {
        navigate(
          replaceIds({
            url: ROUTES.SINGLE_CAMPAIGN_GENERAL,
            params: [
              {
                id: data.influencers[0].value || '',
              },
              { campaignId: res.createCampaign.data || '' },
            ],
          }),
        );
        reset();
        setOpen(false);
        setSelectedBrand(null);
        refetch();
      },
      onError: () => {},
    });
  };

  // `BrandPopUp` fires `setCreatedBrand(id, brand)` right after its create
  // mutation resolves (the popup stays open with its own loading state until
  // then). We select the new brand IMMEDIATELY using the data BrandPopUp
  // already has — no need to wait for a `GET_BRANDS` refetch to find it,
  // which could otherwise miss the brand entirely if the current search
  // text/category filter doesn't match it. We still kick off a background
  // refetch via `ClientSearchPanel` so the list stays consistent if the user
  // searches afterwards.
  const handleBrandCreated = (id: string, brand?: ClientBrandItem) => {
    setOpenCreateBrand(false);
    if (brand) {
      setSelectedBrand(brand);
    }
    clientSearchPanelRef.current?.onBrandCreated(id);
  };

  const closeModal = () => {
    reset();
    setOpen(false);
    setSelectedBrand(null);
    setOpenCreateBrand(false);
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        style="light-blue"
        text={t('campaigns.createCampaign')}
        id="createCampaign"
        iconLeft={<CampaignIcon />}
        customClassnames={`${!checkPermission(PERMISSIONS.CREATE_CAMPAIGN) && 'hidden'}`}
      />
      {open && (
        <PopupLayout
          title={t('campaigns.createCampaignTitle')}
          closeModal={closeModal}
          icon={<CampaignIcon />}
        >
          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-row gap-x-6 mt-5 items-stretch"
            >
              {/* LEFT — Client search (only clients, no prospects) */}
              <ClientSearchPanel
                ref={clientSearchPanelRef}
                selectedBrand={selectedBrand}
                onSelect={setSelectedBrand}
                control={control}
                categoryFieldName="__clientCategory"
              />

              {/* RIGHT — Campaign details */}
              <div className="flex flex-col gap-y-4 w-1/2">
                {/* Selected client read-only + inline create-brand trigger */}
                <div className="flex flex-col gap-y-1">
                  <p className="text-white font-semibold text-sm">
                    {t('campaigns.createClient')}
                  </p>
                  <div className="flex flex-row items-center gap-x-2">
                    <div className="flex-1 flex flex-row items-center gap-x-3 bg-white rounded-[4px] px-3 py-2 min-h-[41px]">
                      {selectedBrand ? (
                        <>
                          <BrandImage
                            image={selectedBrand.profile_picture}
                            size="size-[24px]"
                          />
                          <span className="text-dark-blue text-sm font-medium">
                            {selectedBrand.name}
                          </span>
                        </>
                      ) : (
                        <span className="text-[#5A5A5A] text-sm">
                          {t('campaigns.createClientPlaceholder')}
                        </span>
                      )}
                    </div>
                    {checkPermission(PERMISSIONS.CREATE_BRAND) && (
                      <button
                        type="button"
                        id="createBrandFromCampaign"
                        title={t('brands.createBrandTitle')}
                        aria-label={t('brands.createBrandTitle')}
                        onClick={() => setOpenCreateBrand(true)}
                        className="shrink-0 w-[46px] h-[41px] bg-influentia-light-blue flex items-center justify-center rounded-[4px] cursor-pointer"
                      >
                        <AddClientIcon />
                      </button>
                    )}
                  </div>
                </div>

                <TextInput
                  register={register}
                  registerName="name"
                  type="text"
                  label={t('campaigns.createName')}
                  placeholder={t('campaigns.createNamePlaceholder')}
                  validation={{ required: t('campaigns.nameRequired') }}
                  errors={errors.name}
                />

                <DatePickerInput
                  name="month"
                  monthYearPicker
                  isClearable
                  label={t('campaigns.campaignMonth')}
                  control={control}
                  fullSpace
                  validation={{ required: true }}
                  errors={
                    errors.month
                      ? { message: t('campaigns.campaignMonthError') }
                      : ''
                  }
                />

                <BrandGroupDropdown
                  brand_id={selectedBrand?.id || undefined}
                  label={t('singleCampaign.assignGroup')}
                  name="group_id"
                  control={control}
                  isClearable={false}
                  validation={{ required: t('campaigns.groupRequired') }}
                />

                <UserDropdownList
                  type="influentia"
                  accessName="accountManagers"
                  label={t('campaigns.createAccountManagers')}
                  accessKey="value"
                  addText={t('campaigns.createAccountManagersAdd')}
                  placeholderSelects={t(
                    'campaigns.createAccountManagersPlaceholder',
                  )}
                  control={control}
                  minLength={0}
                />

                <UserDropdownList
                  type="influencer"
                  accessName="influencers"
                  label={t('campaigns.createInfluencers')}
                  accessKey="value"
                  addText={t('campaigns.createInfluencersAdd')}
                  placeholderSelects={t(
                    'campaigns.createInfluencersPlaceholder',
                  )}
                  control={control}
                  error={
                    errors.influencers?.root
                      ? t('campaigns.addInfluencerError')
                      : ''
                  }
                  menuPosition="fixed"
                />

                <div className="flex justify-end mt-auto">
                  <div className="w-[150px]">
                    <Button
                      style="light-blue"
                      id="saveCampaign"
                      text={t('global.save')}
                      customClassnames="w-full justify-center"
                      type="submit"
                      loading={loading}
                    />
                  </div>
                </div>
              </div>
            </form>
          </FormProvider>
        </PopupLayout>
      )}
      {/* Inline brand creation — opens the existing BrandPopUp modal so the
          user can add a brand without leaving the campaign-creation flow.
          Mirrors the influencer inline-creation pattern in UserDropdownList.tsx.
          Guarded with `open` so the brand popup never outlives the parent
          campaign popup. */}
      {open && openCreateBrand && (
        <ModalPortal>
          <BrandPopUp
            setOpen={setOpenCreateBrand}
            redirect={false}
            refetch={() => {}}
            setCreatedBrand={handleBrandCreated}
          />
        </ModalPortal>
      )}
    </>
  );
};
